import isEmpty from 'lodash/fp/isEmpty';
import React, { ReactNode } from 'react';
import './IncentivesTable.scss';
import { Dimmer, Loader } from 'semantic-ui-react';

export type Item = {
  className?: string;
  content: ReactNode;
};

export type IncentivesTableProps = {
  className: string;
  emptyMessage: string;
  footers: Item[][];
  headers: Item[];
  onRowClick?: (row: Item[], i: number, e: React.MouseEvent) => void;
  rows: Item[][];
  isLoading?: boolean;
};

export default function IncentivesTable({
  className,
  emptyMessage,
  footers,
  headers,
  onRowClick,
  rows,
  isLoading,
}: IncentivesTableProps) {
  const renderHeader = (item: Item, key: number) => (
    <th className={item.className} key={key}>
      {item.content}
    </th>
  );

  const renderItem = (item: Item, key: number) => (
    <td className={item.className} key={key}>
      {item.content}
    </td>
  );

  const renderRow = (row: Item[], i: number) => (
    <tr key={i} onClick={e => onRowClick && onRowClick(row, i, e)}>
      {row.map(renderItem)}
    </tr>
  );

  const renderEmpty = (colspan: number, message: string) => (
    <tr>
      <td colSpan={colspan}>{message}</td>
    </tr>
  );

  const renderBody = () => {
    if (isEmpty(rows)) {
      return renderEmpty(headers.length, emptyMessage);
    }

    return rows.map(renderRow);
  };

  const renderLoadingCompatibleIncentives = () => {
    if (isLoading) {
      return (
        <Dimmer active inverted>
          <Loader inverted content="Loading compatible incentives..." />
        </Dimmer>
      );
    }

    return null;
  };

  const renderFooter = () => {
    if (isEmpty(footers)) {
      return null;
    }

    return footers.map(renderRow);
  };

  const computeTableContainerMinWidth = () => {
    const numColumns = headers.length;
    // 84 for the first column, 250 for the second column, 115 for each additional column
    const minWidth = 84 + 250 + numColumns * 115;
    return `${minWidth}px`;
  };

  const dataTableContainerStyle = {
    minWidth: computeTableContainerMinWidth(),
  };

  return (
    <div
      className={`data-table-container ${className}`}
      style={dataTableContainerStyle}
    >
      {renderLoadingCompatibleIncentives()}
      <table className="data-table" aria-label="edit-incentives-table">
        <thead aria-label="header">
          <tr>{headers.map(renderHeader)}</tr>
        </thead>
        <tbody aria-label="body">{renderBody()}</tbody>
        <tfoot aria-label="footer">{renderFooter()}</tfoot>
      </table>
    </div>
  );
}
